import "./SalesPage.css";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type SaleStatus =
  | "draft"
  | "confirmed"
  | "partially_paid"
  | "paid"
  | "cancelled";

type SaleRow = {
  id: string;
  sale_number: string;
  status: SaleStatus;
  issue_date: string;
  currency_code: string;
  subtotal: number | string;
  discount_total: number | string;
  tax_total: number | string;
  total: number | string;
  paid_total: number | string;
  balance: number | string;
  customer_id: string | null;
  customer_name: string | null;
  quotation_id: string | null;
  service_order_id: string | null;
  updated_at: string;
};

type CustomerOption = {
  id: string;
  name: string;
  status: "active" | "inactive" | "archived";
};

type CatalogOption = {
  id: string;
  item_type: "product" | "service";
  sku: string | null;
  name: string;
  sale_price: number | string;
  tax_rate: number | string;
  status: "active" | "inactive" | "archived";
};

type SaleLine = {
  catalogItemId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  discountAmount: string;
  taxRate: string;
};

const STATUS_LABELS: Record<SaleStatus, string> = {
  draft: "Borrador",
  confirmed: "Confirmada",
  partially_paid: "Pago parcial",
  paid: "Pagada",
  cancelled: "Cancelada",
};

function limaDate() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function emptyLine(): SaleLine {
  return {
    catalogItemId: "",
    description: "",
    quantity: "1",
    unitPrice: "0",
    discountAmount: "0",
    taxRate: "18",
  };
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function lineTotal(line: SaleLine) {
  const quantity = Number(line.quantity || 0);
  const unitPrice = Number(line.unitPrice || 0);
  const discount = Number(line.discountAmount || 0);
  const taxRate = Number(line.taxRate || 0);
  const gross = Math.max(quantity * unitPrice, 0);
  const net = Math.max(gross - discount, 0);
  return net + net * taxRate / 100;
}

export function SalesPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [sales, setSales] = useState<SaleRow[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [catalog, setCatalog] = useState<CatalogOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState(limaDate());
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<SaleLine[]>([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const loadData = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setSales([]);
        setCustomers([]);
        setCatalog([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const [salesResult, customersResult, catalogResult] = await Promise.all([
        supabase.rpc("list_organization_sales", {
          p_organization_id: organizationId,
          p_search: query.trim() || null,
        }),
        supabase.rpc("list_organization_customers", {
          p_organization_id: organizationId,
          p_search: null,
        }),
        supabase.rpc("list_organization_catalog", {
          p_organization_id: organizationId,
          p_search: null,
          p_item_type: null,
        }),
      ]);

      if (salesResult.error || customersResult.error || catalogResult.error) {
        setLoadError(
          salesResult.error?.message ??
            customersResult.error?.message ??
            catalogResult.error?.message ??
            "No se pudo cargar Ventas.",
        );
      }

      setSales(
        salesResult.error ? [] : ((salesResult.data ?? []) as SaleRow[]),
      );
      setCustomers(
        customersResult.error
          ? []
          : ((customersResult.data ?? []) as CustomerOption[]).filter(
              (row) => row.status === "active",
            ),
      );
      setCatalog(
        catalogResult.error
          ? []
          : ((catalogResult.data ?? []) as CatalogOption[]).filter(
              (row) => row.status === "active",
            ),
      );
      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadData(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadData, search]);

  useEffect(() => {
    setSearch("");
    setFormOpen(false);
    setCustomerId("");
    setIssueDate(limaDate());
    setNotes("");
    setLines([emptyLine()]);
    setActionError(null);
    setActionSuccess(null);
  }, [organizationId]);

  const estimate = useMemo(
    () => lines.reduce((sum, line) => sum + lineTotal(line), 0),
    [lines],
  );

  const openCount = useMemo(
    () => sales.filter((sale) => !["paid", "cancelled"].includes(sale.status)).length,
    [sales],
  );

  function updateLine(index: number, patch: Partial<SaleLine>) {
    setLines((current) =>
      current.map((line, currentIndex) =>
        currentIndex === index ? { ...line, ...patch } : line,
      ),
    );
  }

  function selectCatalog(index: number, catalogItemId: string) {
    const item = catalog.find((row) => row.id === catalogItemId);

    if (!item) {
      updateLine(index, { catalogItemId: "" });
      return;
    }

    updateLine(index, {
      catalogItemId: item.id,
      description: item.name,
      unitPrice: String(item.sale_price ?? 0),
      taxRate: String(item.tax_rate ?? 0),
    });
  }

  async function createSale(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;

    const payload = lines.map((line) => ({
      catalog_item_id: line.catalogItemId || null,
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unit_price: Number(line.unitPrice),
      discount_amount: Number(line.discountAmount),
      tax_rate: Number(line.taxRate),
    }));

    const invalid = payload.some(
      (line) =>
        !line.description ||
        !Number.isFinite(line.quantity) ||
        line.quantity <= 0 ||
        !Number.isFinite(line.unit_price) ||
        line.unit_price < 0 ||
        !Number.isFinite(line.discount_amount) ||
        line.discount_amount < 0 ||
        line.discount_amount > line.quantity * line.unit_price ||
        !Number.isFinite(line.tax_rate) ||
        line.tax_rate < 0 ||
        line.tax_rate > 100,
    );

    if (invalid) {
      setActionError("Revisa las líneas de la venta.");
      return;
    }

    setSaving(true);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("create_organization_sale", {
      p_organization_id: organizationId,
      p_customer_id: customerId || null,
      p_issue_date: issueDate,
      p_currency_code: "PEN",
      p_notes: notes.trim() || null,
      p_items: payload,
    });

    setSaving(false);

    if (error) {
      setActionError(error.message);
      return;
    }

    setCustomerId("");
    setIssueDate(limaDate());
    setNotes("");
    setLines([emptyLine()]);
    setFormOpen(false);
    setActionSuccess("Venta creada en borrador.");
    await loadData(search);
  }

  async function changeStatus(saleId: string, status: "confirmed" | "cancelled") {
    setBusyId(saleId);
    setActionError(null);
    setActionSuccess(null);

    const { error } = await supabase.rpc("transition_sale_status", {
      p_sale_id: saleId,
      p_new_status: status,
    });

    setBusyId(null);

    if (error) {
      setActionError(error.message);
      return;
    }

    setActionSuccess(
      status === "confirmed" ? "Venta confirmada." : "Venta cancelada.",
    );
    await loadData(search);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con Ventas.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Ventas</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Ventas, cobros e integración con inventario.</p>
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setFormOpen((value) => !value);
            setActionError(null);
            setActionSuccess(null);
          }}
        >
          {formOpen ? "Cerrar" : "Nueva venta"}
        </button>
      </header>

      {actionError ? (
        <div className="inline-alert" role="alert">
          {actionError}
        </div>
      ) : null}

      {actionSuccess ? (
        <div className="form-success sales-message">{actionSuccess}</div>
      ) : null}

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Registro</span>
              <h2>Nueva venta</h2>
            </div>
            <strong className="sales-estimate">{money(estimate)}</strong>
          </div>

          <form className="sale-form" onSubmit={createSale}>
            <div className="sale-header-grid">
              <label>
                Cliente
                <select
                  value={customerId}
                  onChange={(event) => setCustomerId(event.target.value)}
                >
                  <option value="">Consumidor / sin cliente</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha
                <input
                  type="date"
                  required
                  value={issueDate}
                  onChange={(event) => setIssueDate(event.target.value)}
                />
              </label>

              <label className="field-span-2">
                Notas
                <input
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
            </div>

            <div className="sale-lines-heading">
              <strong>Detalle</strong>
              <button
                className="compact-button"
                type="button"
                onClick={() => setLines((current) => [...current, emptyLine()])}
              >
                + Línea
              </button>
            </div>

            <div className="sale-lines">
              {lines.map((line, index) => (
                <div className="sale-line" key={index}>
                  <label className="sale-line-catalog">
                    Catálogo
                    <select
                      value={line.catalogItemId}
                      onChange={(event) => selectCatalog(index, event.target.value)}
                    >
                      <option value="">Libre</option>
                      {catalog.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sku ? `${item.sku} · ` : ""}
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="sale-line-description">
                    Descripción
                    <input
                      required
                      value={line.description}
                      onChange={(event) =>
                        updateLine(index, { description: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Cant.
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={line.quantity}
                      onChange={(event) =>
                        updateLine(index, { quantity: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Precio
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unitPrice}
                      onChange={(event) =>
                        updateLine(index, { unitPrice: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Desc.
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.discountAmount}
                      onChange={(event) =>
                        updateLine(index, { discountAmount: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    IGV %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.taxRate}
                      onChange={(event) =>
                        updateLine(index, { taxRate: event.target.value })
                      }
                    />
                  </label>

                  <div className="sale-line-total">
                    <span>Total</span>
                    <strong>{money(lineTotal(line))}</strong>
                  </div>

                  <button
                    className="danger-ghost-button"
                    type="button"
                    disabled={lines.length === 1}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((_, currentIndex) => currentIndex !== index),
                      )
                    }
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>

            <div className="form-actions">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Guardar borrador"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : sales.length}</strong>
          <span> ventas · {openCount} abiertas</span>
        </div>

        <input
          type="search"
          placeholder="Buscar por venta o cliente"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && sales.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay ventas.</strong>
          <p>Crea la primera venta para comenzar.</p>
        </section>
      ) : null}

      <section className="sales-grid" aria-label="Ventas">
        {sales.map((sale) => (
          <article className="sale-card" key={sale.id}>
            <div className="sale-card-heading">
              <div>
                <span className={`status-badge sale-status-${sale.status}`}>
                  {STATUS_LABELS[sale.status]}
                </span>
                <h2>{sale.sale_number}</h2>
                <p>{sale.customer_name ?? "Sin cliente"}</p>
              </div>
              <strong>{money(sale.total, sale.currency_code)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Fecha</dt>
                <dd>{sale.issue_date}</dd>
              </div>
              <div>
                <dt>Pagado</dt>
                <dd>{money(sale.paid_total, sale.currency_code)}</dd>
              </div>
              <div>
                <dt>Saldo</dt>
                <dd>{money(sale.balance, sale.currency_code)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(sale.tax_total, sale.currency_code)}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link className="compact-button" to={`/ventas/${sale.id}`}>
                Abrir
              </Link>

              {sale.status === "draft" ? (
                <button
                  className="compact-button"
                  type="button"
                  disabled={busyId === sale.id}
                  onClick={() => void changeStatus(sale.id, "confirmed")}
                >
                  {busyId === sale.id ? "Procesando…" : "Confirmar"}
                </button>
              ) : null}

              {["draft", "confirmed"].includes(sale.status) &&
              Number(sale.paid_total) === 0 ? (
                <button
                  className="compact-button compact-button-danger"
                  type="button"
                  disabled={busyId === sale.id}
                  onClick={() => void changeStatus(sale.id, "cancelled")}
                >
                  Cancelar
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
